import { useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ComboBox } from "~/components/common/combo-box";
import { ContentContainer } from "~/components/common/content-container";
import { Dialog } from "~/components/common/Dialog";
import { Button } from "~/components/ui/button";
import type { Contact, ContactCreateDto } from "~/types/api";
import type {
  BulkFormData,
  FormOption,
  FormStructure,
  TabDef,
  UseStructureResult,
} from "~/types/ui";
import { SoForm } from "../so-form";
import { Loading } from "~/components/common/loading";
import { ContactApi } from "~/api/contact-api";
import { toast } from "sonner";
import { FormFieldType } from "../fields";
import z from "zod";
import { TabedComponent } from "../tabed-form";
import { isBulkFormData } from "~/utils/form-util";

export function ContactSelect({
  value,
  onChange,
  options,
  canCreate = false,
}: {
  value: Contact[];
  onChange: (val: Contact[]) => void;
  options: Contact[];
  canCreate?: boolean;
}) {
  const { t } = useTranslation();
  const selectedValue =
    value?.filter((x) => x.contactId).map((x) => x.contactId as string) ?? [];

  function handleOnChange(value: string[]) {
    onChange?.(
      options.filter((x) => x.contactId && value.includes(x.contactId)),
    );
  }

  return (
    <div className="flex w-auto">
      <div className="w-full">
        <ComboBox
          options={options?.map(
            (d) =>
              ({
                label: d.name,
                description: `${d.email}\n ${d.phoneNumber}`,
                value: d.contactId,
              }) as FormOption<any>,
          )}
          value={selectedValue}
          onChange={(val) => handleOnChange(val as string[])}
          placeholder={t("actions.placeholders.contacts")}
          clearable={true}
          isMulti
        />
      </div>
      {canCreate && <ContactCreateDialog />}
    </div>
  );
}

export function ContactCreateDialog() {
  const { t } = useTranslation();
  const [contactDialog, setContactDialog] = useState<boolean>(false);

  return (
    <Dialog
      title={t("actions.contacts.title")}
      description={t("actions.contacts.description")}
      width="70vw"
      maxWidth="70vw"
      open={contactDialog}
      onOpenChange={setContactDialog}
      trigger={
        <Button variant="ghost" aria-label="Edit Selected">
          <Plus className="h-[1.2rem] w-[1.2rem] text-green-600 " />
        </Button>
      }
      children={
        <ContentContainer>
          <ContactCreate onCreate={() => setContactDialog(false)} />
        </ContentContainer>
      }
    />
  );
}

export function ContactCreate({
  data = {},
  onCreate,
}: {
  data?: ContactCreateDto;
  onCreate: (
    contact: ContactCreateDto | BulkFormData<ContactCreateDto>,
  ) => void;
}) {
  const { t } = useTranslation();

  const tabs: TabDef[] = [
    {
      value: "single",
      labelKey: "single",
      content: (
        <ContactCreateForm data={data} onCreate={(data) => onCreate(data)} />
      ),
    },
    {
      value: "bulk",
      labelKey: "bulk",
      content: (
        <ContactCreateForm
          data={data}
          onCreate={(data) => onCreate(data)}
          bulk
        />
      ),
    },
  ];

  return (
    <TabedComponent
      tabsTranslationSection={"common.tabs"}
      tabs={tabs}
      translationResource={t("actions.contacts.nav")}
    />
  );
}

export function ContactCreateForm({
  data = {},
  onCreate,
  bulk = false,
}: {
  data?: ContactCreateDto;
  onCreate: (
    contact: ContactCreateDto | BulkFormData<ContactCreateDto>,
  ) => void;
  bulk?: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const structure = useContactStructure(data);

  async function handleSubmit(
    data: ContactCreateDto | BulkFormData<ContactCreateDto>,
  ) {
    const contactApi = new ContactApi();
    const result = await (isBulkFormData(data)
      ? contactApi.createContacts(data.items as ContactCreateDto[])
      : contactApi.createContact(data as ContactCreateDto));
    if (!result.ok) {
      toast.error(t("common.errors.general"));
      console.error("Failed to create new Contact(s)", result.error);
      return;
    }
    toast.success(t("common.submitted"));
    queryClient.invalidateQueries({ queryKey: ["contacts:all"] });
    onCreate(data);
  }

  if (structure.isLoading || Object.keys(structure.structure).length === 0)
    return <Loading />;

  return (
    <SoForm
      structure={structure.structure}
      onSubmit={handleSubmit}
      isBulkForm={bulk}
    />
  );
}

function useContactStructure(
  data: ContactCreateDto,
): UseStructureResult<ContactCreateDto> {
  const isLoading = false;
  const structure: FormStructure<ContactCreateDto> = {
    name: {
      type: FormFieldType.TEXT,
      label: "actions.contacts.fields.name",
      placeholder: "actions.contacts.placeholders.name",
      default: data?.name || "",
      rules: z.string().min(2, "actions.validation.nameRequired"),
    },
    email: {
      type: FormFieldType.EMAIL,
      label: "actions.contacts.fields.email",
      placeholder: "actions.contacts.placeholders.email",
      default: data?.email || "",
      rules: z.string().min(2, "actions.validation.emailRequired"),
    },
    phoneNumber: {
      type: FormFieldType.NUMBER,
      label: "actions.contacts.fields.phoneNumber",
      placeholder: "actions.contacts.placeholders.phoneNumber",
      default: data?.phoneNumber || "",
      rules: z.string().min(2, "actions.validation.phoneNumberRequired"),
    },
  };

  return { structure, isLoading };
}
